import React, { useState } from "react";
import { useSearchFields } from "../../../common/hooks/useSearchFields";
import {
  Filter,
  TextInput,
  Datagrid,
  TextField,
  DateField,
  Identifier,
  FunctionField,
  Responsive,
  SimpleList,
  Record,
} from "react-admin";
import { Void } from "../../common/data-grid-fields/Void";
import { Theme, Box, Tooltip, Typography } from "@material-ui/core";
import { useTheme } from "@material-ui/styles";
import { AppbarGroupsFilter } from "../../common/data-grid-inputs/AppBarGroupsFilter";
import { EldDisconnectionListDto } from "./types";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import TimeField from "../../common/data-grid-fields/TimeField";
import { GenericDateOrTimePicker } from "../../common/form/GenericDateOrTimePicker";
import { DatePicker } from "@material-ui/pickers";
import { endOfToday, addDays } from "date-fns";
import BluetoothAudioIcon from "@material-ui/icons/BluetoothAudio";
import BluetoothDisabledIcon from "@material-ui/icons/BluetoothDisabled";
import eldDisconnections from ".";
import { ProLogsEmpty } from "../../common/list/ProLogsEmpty";
import { DefaultProLogsList } from "../../common/list/DefaultProLogsList";
import { useResetListSearchField } from "../../../common/hooks/useResetListSearchField";

const ListFilter = (props: any) => {
  useSearchFields(props, ["vehicleName", "driverFullName", "customDriverId"]);
  useResetListSearchField(props);
  return (
    <Filter {...props}>
      <TextInput
        className="list-view-filter search-box"
        label="resources.common.labels.search"
        source="q"
        alwaysOn
      />
      <GenericDateOrTimePicker
        PickerComponent={DatePicker}
        options={{
          autoOk: true,
        }}
        className="list-view-filter"
        label="resources.hos-eld-disconnections.labels.dateFrom"
        source="dateFrom"
        alwaysOn
      />
      <GenericDateOrTimePicker
        PickerComponent={DatePicker}
        options={{
          autoOk: true,
        }}
        className="list-view-filter"
        label="resources.hos-eld-disconnections.labels.dateTo"
        source="dateTo"
        alwaysOn
      />
    </Filter>
  );
};

const renderLastPositionName = (
  record: EldDisconnectionListDto | undefined,
  theme: Theme
) => {
  if (!record) {
    return null;
  }

  const lastPositionName: string = record.currentVehicleLocation;
  const split = lastPositionName?.split(",") ?? [];
  const rightChildren = split.map((fragment, index) => (
    <div key={index}>{fragment}</div>
  ));
  return (
    <div className="last-position-container">
      <div style={{ color: theme.palette.info.main }}>
        <LocationOnIcon />
      </div>
      <div>
        <Responsive
          medium={<>{rightChildren}</>}
          small={<>{lastPositionName}</>}
        ></Responsive>
      </div>
    </div>
  );
};

export const EldDisconnectionsList = (props: any) => {
  const theme: Theme = useTheme();
  const [selectedGroup, setSelectedGroup] = useState<Identifier>("");

  interface SimpleListProps {
    record?: Record;
  }

  const RenderPrimaryTextForSimpleList = ({ record }: SimpleListProps) => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          gap: "10px",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        {record?.vehicleName + " | " + record?.driverFullName}
      </div>
    );
  };

  const RenderSecondaryTextForSimpleList = ({ record }: SimpleListProps) => {
    return (
      <div
        style={{
          marginTop: "14px",
          display: "flex",
          flexDirection: "column",
          gap: "6px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1px",
            flexWrap: "wrap",
          }}
        >
          <Typography variant="body2" style={{ fontWeight: "600" }}>
            Last disconnected event time:
          </Typography>
          <DateField
            source="lastDisconnectedEventTime"
            label="resources.hos-eld-disconnections.labels.lastDisconnectedEventTime"
            showTime
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1px",
            flexWrap: "wrap",
          }}
        >
          <Typography variant="body2" style={{ fontWeight: "600" }}>
            Last disconnected duration:
          </Typography>
          <TimeField
            record={record}
            source="lastDisconnectedDurationMinutes"
            label="resources.hos-eld-disconnections.labels.lastDisconnectedDurationMinutes"
            inputType="minutes"
            outputType="long"
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "12px",
            alignItems: "center",
            justifyContent: "flex-end",
            width: "100%",
          }}
        >
          <FunctionField<EldDisconnectionListDto>
            source="currentVehicleLocation"
            render={(record) => renderLastPositionName(record, theme)}
            label="Last position"
          />
          <FunctionField<EldDisconnectionListDto>
            source="isConnectedNow"
            headerClassName="centered-content"
            label="resources.hos-eld-disconnections.labels.isConnectedNow"
            render={(record) => (
              <Tooltip title="Current ELD connection status">
                <Box className="centered-content">
                  {record?.isConnectedNow ? (
                    <BluetoothAudioIcon
                      style={{
                        color: theme.palette.success.main,
                      }}
                    />
                  ) : (
                    <BluetoothDisabledIcon
                      style={{
                        color: theme.palette.error.main,
                      }}
                    />
                  )}
                </Box>
              </Tooltip>
            )}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="content-container">
      <AppbarGroupsFilter
        selectedGroup={selectedGroup}
        onSelectedGroupChange={(id) => setSelectedGroup(id)}
      />
      <DefaultProLogsList
        title="resources.hos-eld-disconnections.name"
        actions={<Void />}
        filters={<ListFilter />}
        filter={!!selectedGroup ? { groupId: selectedGroup } : {}}
        filterDefaultValues={{
          dateFrom: addDays(endOfToday(), -7).toISOString(),
          dateTo: endOfToday().toISOString(),
        }}
        bulkActionButtons={false}
        empty={<ProLogsEmpty iconType={eldDisconnections.icon} />}
        sort={{ field: "vehicleName", order: "ASC" }}
        {...props}
      >
        <Responsive
          small={
            <SimpleList
              className="simple-list-wrapper"
              primaryText={(record) => (
                <RenderPrimaryTextForSimpleList record={record} />
              )}
              secondaryText={(record) => (
                <RenderSecondaryTextForSimpleList record={record} />
              )}
              linkType={false}
            />
          }
          medium={
            <Datagrid>
              <TextField
                source="vehicleName"
                label="resources.hos-eld-disconnections.labels.vehicleName"
              />
              <TextField
                source="driverFullName"
                label="resources.hos-eld-disconnections.labels.driverFullName"
              />
              <DateField
                source="lastDisconnectedEventTime"
                label="resources.hos-eld-disconnections.labels.lastDisconnectedEventTime"
                showTime
              />
              <TimeField
                source="lastDisconnectedDurationMinutes"
                label="resources.hos-eld-disconnections.labels.lastDisconnectedDurationMinutes"
                inputType="minutes"
                outputType="long"
              />
              <FunctionField<EldDisconnectionListDto>
                source="isConnectedNow"
                headerClassName="centered-content"
                label="resources.hos-eld-disconnections.labels.isConnectedNow"
                render={(record) => (
                  <Box className="centered-content">
                    {record?.isConnectedNow ? (
                      <BluetoothAudioIcon
                        style={{
                          color: theme.palette.success.main,
                        }}
                      />
                    ) : (
                      <BluetoothDisabledIcon
                        style={{
                          color: theme.palette.error.main,
                        }}
                      />
                    )}
                  </Box>
                )}
              />
              <FunctionField<EldDisconnectionListDto>
                source="currentVehicleLocation"
                render={(record) => renderLastPositionName(record, theme)}
                label="Last position"
              />
            </Datagrid>
          }
        />
      </DefaultProLogsList>
    </div>
  );
};
